// ============================================================
// HIT SOAK - The middle of one hit: what stands between the raw
// damage and the wound. Cover, worn/machine/natural armour, the
// weapon's penetration, Toughness (Felling, daemonic protection,
// Withering's drain), the protagonist soak cap, and whether the
// target can be touched at all (Incorporeal). Pure: no dice.
// ============================================================
import { areaOf } from './areaShapes';
import { hasWS, getWSP } from './weaponSpecials';
import { soakDrain } from './statDrains';
import { resolveArmourAP, isLocCoveredForBody } from './bodyConfig';
import { effectiveVehicleFacing } from './vehicleFacing';
import type { Cover, Target } from '../types';
import type { CalcSingleHitParams, HitContext } from './hitDamageTypes';

/** The soak side of a hit, in `HitResult`'s field names. */
export interface HitSoak {
  ap: number;
  effectiveAP: number;
  coverAP: number;
  tb: number;
  baseAP: number;
  machineAP: number;
  naturalAP: number;
  effectivePen: number;
  utMultiplier: number;
  fellingReduction: number;
  daemonicProt: number;
  soakCapReduction: number;
  /** Force's psy rating on damage (Channeling included); the pen half is already in `effectivePen`. */
  forceBonus: number;
  /** Incorporeal, and nothing in the weapon can touch it: the hit deals 0. */
  blocked: boolean;
}

const has = (sp: string[], ids: readonly string[]): boolean => ids.some((id) => hasWS(sp, id));

// Daemonic protection: bypassed by Sanctified, Force, Warp weapons -
// or Baleful, which bypasses THIS alone and nothing else (v8.284.0).
const DAEMONIC_BYPASS = ['Sanctified', 'Force', 'WarpWeapon', 'Baleful'] as const;
// WarpWeapon/Graviton bypass armour outright; Armourbane is the MUNDANE
// version - AP 0 and nothing else: no daemonic bypass, no incorporeal
// touch, no Larraman clause (v8.284.0). Lance fights armour through its
// DoS-multiplied Pen instead.
const ARMOUR_BYPASS = ['WarpWeapon', 'Graviton', 'Armourbane'] as const;
// Incorporeal: only damaged by Force, Warp, Sanctified - or Exorcist,
// the blessed shells that open THIS gate alone (v8.284.0).
const TOUCHES_INCORPOREAL = ['Force', 'WarpWeapon', 'Sanctified', 'Exorcist'] as const;
// Spray always ignored cover; Coverbane exposes that as a rule of its
// own, and Seeking (the no-LoS smart round, v8.291.0) shares it.
const COVER_BYPASS = ['Coverbane', 'Seeking'] as const;

/**
 * BOTH COVERS ARE ASKED ABOUT THIS LOCATION, and the better one answers.
 * `coverAlt` is only ever set under the "best for each body part" ruling
 * (see `engine/coverConflict.ts`); everywhere else it is undefined and
 * this is the single check it always was. The template's cover bypass
 * (v8.372.4; Spray's rider until then), Coverbane's and Seeking's zero it.
 */
function coverAPOf(params: CalcSingleHitParams, ctx: HitContext): number {
  const { slot, target, location } = params;
  const ignoresCover = (params.coverBypass ?? areaOf(slot).coverBypass) || has(ctx.sp, COVER_BYPASS);
  if (ignoresCover) return 0;
  const coverAtHit = (c: Cover | undefined | null) =>
    c && isLocCoveredForBody(c.level || 'none', location.id, ctx.bodyConfig) ? c.ap || 0 : 0;
  return Math.max(coverAtHit(target.cover), coverAtHit(target.coverAlt));
}

/**
 * The three armour components. Scatter at distance: WORN armour doubles
 * (base AP only - machine and natural toughness are flesh and steel, not
 * a flak vest catching shot). Breaching/Fleshbane (v8.284.0): each
 * zeroes exactly ONE component.
 */
function armourOf(params: CalcSingleHitParams, ctx: HitContext) {
  const { target, location, rangeBand } = params;
  const scatterDoubled = hasWS(ctx.sp, 'Scatter') && (rangeBand === 'long' || rangeBand === 'extreme');
  const worn = resolveArmourAP(target.armour, location.id, ctx.bodyConfig, effectiveVehicleFacing(target));
  const ignoreNatural = hasWS(ctx.sp, 'Fleshbane') || ctx.exposed.ignoreNatural;
  return {
    baseAP: scatterDoubled ? worn * 2 : worn,
    machineAP: hasWS(ctx.sp, 'Breaching') ? 0 : target.machineAP || 0,
    naturalAP: ignoreNatural ? 0 : target.naturalArmour || 0,
  };
}

/**
 * Official Force: the psy rating reaches Penetration as well as damage
 * (Pen half added v8.197.5). Channeling(X) (v8.284.0) adds stored charge
 * at BOTH sites - and can lend a rating to a wielder who has none.
 */
function forceBonusOf(params: CalcSingleHitParams, sp: string[]): number {
  const forcePsy = (params.slot.psyRating || 0) + getWSP(sp, 'Channeling');
  return hasWS(sp, 'Force') && forcePsy > 0 ? forcePsy : 0;
}

/**
 * Effective penetration: Melta at short range doubles it; official Only
 * War Lance multiplies it by the attack's DoS - armour matters again,
 * skill melts it (replaced flat AP=0, v8.197.4); Maximal adds 2; then
 * Force, Witchbane/Purifier (v8.285.0) and Breached (v8.350.0, the
 * target's own exposure opening its armour).
 */
function effectivePenOf(params: CalcSingleHitParams, ctx: HitContext, predicatePen: number, forceBonus: number) {
  const { pen, dos, isShortOrCloser } = params;
  let effectivePen = pen;
  if (hasWS(ctx.sp, 'Melta') && isShortOrCloser) effectivePen = pen * 2;
  if (hasWS(ctx.sp, 'Lance')) effectivePen = pen * Math.max(1, dos);
  if (hasWS(ctx.sp, 'Maximal')) effectivePen += 2;
  effectivePen += forceBonus;
  effectivePen += predicatePen;
  effectivePen += ctx.exposed.pen;
  return effectivePen;
}

/**
 * Toughness with Felling and daemonic protection. Withering's drain bites
 * the SOAK (v8.294.0) - the one drained number that is arithmetic rather
 * than a test, so no condition modifier can reach it. Off the bonus
 * before the unnatural multiplier, which is what makes a drained
 * Toughness worth less to a monster too. An `ignoreTB` exposure
 * (v8.350.0) removes the man's own toughness; daemonic protection is not
 * his. A `tb` exposure (v8.368.1): their toughness counts for less (or
 * more) as the hit sees it.
 */
function toughnessOf(target: Target, ctx: HitContext) {
  const rawUtMult = target.unnaturalToughness || 1;
  const fellingReduction = hasWS(ctx.sp, 'Felling') ? getWSP(ctx.sp, 'Felling') : 0;
  const utMultiplier = fellingReduction > 0 ? Math.max(1, rawUtMult - fellingReduction) : rawUtMult;
  const daemonicProt = has(ctx.sp, DAEMONIC_BYPASS) ? 0 : target.daemonicProtection || 0;
  const drainedTB = Math.max(0, target.toughnessBonus - soakDrain(target.conditions));
  const tb = (ctx.exposed.ignoreTB ? 0 : drainedTB * utMultiplier) + daemonicProt;
  return { tb: ctx.exposed.tb ? Math.max(0, tb + ctx.exposed.tb) : tb, utMultiplier, fellingReduction, daemonicProt };
}

interface OwnSoak {
  baseAP: number;
  machineAP: number;
  naturalAP: number;
  tb: number;
}

/**
 * THE SOAK CAP - HIS OWN SOAK ONLY. Base, machine and natural armour plus
 * Toughness. COVER IS ADDED ON TOP AND IS NEVER CAPPED.
 *
 * Capping the raw sum instead would have been a quiet disaster: power
 * armour plus Astartes Toughness is already 18, so any sensible ceiling
 * is reached before cover is even considered, and a marine ducking
 * behind a barricade would get exactly the numbers he had in the open
 * while the log went on printing the cover it granted. It is also
 * location-dependent - cover would appear to work on a leg and not on a
 * head within one burst - which reads as a bug rather than a ruling.
 *
 * Spent against armour before Toughness: the plate is equipment, the
 * Toughness is the man. Each component is reduced in turn (base, natural,
 * machine, then TB) rather than the total, so the breakdown's arithmetic
 * still adds up in the log.
 */
function capOwnSoak(own: OwnSoak, cap: number | undefined): OwnSoak & { soakCapReduction: number } {
  if (cap === undefined) return { ...own, soakCapReduction: 0 };
  let excess = Math.max(0, own.baseAP + own.machineAP + own.naturalAP + own.tb - cap);
  let soakCapReduction = 0;
  const spend = (value: number): number => {
    const taken = Math.min(excess, value);
    excess -= taken;
    soakCapReduction += taken;
    return value - taken;
  };
  const baseAP = spend(own.baseAP);
  const naturalAP = spend(own.naturalAP);
  const machineAP = spend(own.machineAP);
  const tb = spend(own.tb);
  return { baseAP, machineAP, naturalAP, tb, soakCapReduction };
}

/** Everything that soaks this hit, and how much of it the weapon punches through. */
export function soakOf(params: CalcSingleHitParams, ctx: HitContext, predicatePen: number): HitSoak {
  const coverAP = coverAPOf(params, ctx);
  const forceBonus = forceBonusOf(params, ctx.sp);
  const effectivePen = effectivePenOf(params, ctx, predicatePen, forceBonus);
  const { tb: rawTB, ...toughness } = toughnessOf(params.target, ctx);
  const own = capOwnSoak({ ...armourOf(params, ctx), tb: rawTB }, ctx.prot?.soakCap);
  const ap = own.baseAP + own.machineAP + own.naturalAP + coverAP;
  const effectiveAP = has(ctx.sp, ARMOUR_BYPASS) ? 0 : Math.max(0, ap - effectivePen);
  const blocked = !!params.target.incorporeal && !has(ctx.sp, TOUCHES_INCORPOREAL);
  return { ...own, ...toughness, ap, effectiveAP, coverAP, effectivePen, forceBonus, blocked };
}
